/**
 * AnalyticsService — provider-agnostic event tracking.
 *
 * Defines a standard event taxonomy. No analytics vendor is
 * hardcoded — any provider (PostHog, Mixpanel, GA4, etc.) can
 * be plugged in by implementing the AnalyticsProvider interface.
 */

import { LoggerFactory } from '@/lib/logging';
import type { LogContext } from '@/lib/logging';

export type AnalyticsEventName =
  | 'auth_login'
  | 'auth_logout'
  | 'auth_register'
  | 'auth_login_failed'
  | 'auth_password_reset'
  | 'auth_verification_email'
  | 'auth_session_refresh'
  | 'auth_session_expired'
  | 'draft_created'
  | 'draft_updated'
  | 'application_submitted'
  | 'profile_updated'
  | 'workspace_page_view'
  | 'workspace_navigation'
  | 'review_queue_opened'
  | 'review_opened'
  | 'review_approved'
  | 'review_rejected'
  | 'review_status_change'
  | 'search_performed'
  | 'search_filtered'
  | 'registry_opened'
  | 'registry_search'
  | 'registry_filter'
  | 'creator_profile_opened'
  | 'page_view'
  | 'route_change'
  | 'protected_redirect'
  | 'not_found'
  | 'email_queued'
  | 'email_sent'
  | 'email_delivered'
  | 'email_failed'
  | 'email_retry'
  | 'email_open'
  | 'email_click'
  | 'upload_started'
  | 'upload_completed'
  | 'upload_failed'
  | 'upload_cancelled'
  | 'file_download'
  | 'preview_opened'
  | 'notification_created'
  | 'notification_read'
  | 'notification_all_read'
  | 'notification_preferences_updated'
  | 'referral_link_copied'
  | 'referral_shared'
  | 'creator_id_copied'
  | 'public_profile_link_copied'
  | 'registration_cta_clicked'
  | 'registration_started'
  | 'application_started'
  | 'dashboard_cta_clicked'
  | 'resource_center_opened'
  | 'resource_opened'
  | 'resource_category_selected'
  | 'notification_opened'
  | 'notification_marked_read';

export interface AnalyticsEvent {
  name: AnalyticsEventName;
  properties?: Record<string, unknown>;
  timestamp: string;
  context?: LogContext;
}

export interface AnalyticsProvider {
  track(event: AnalyticsEvent): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  reset(): void;
}

class ConsoleAnalyticsProvider implements AnalyticsProvider {
  private logger = LoggerFactory.create();

  track(event: AnalyticsEvent): void {
    this.logger.debug(`[analytics] ${event.name}`, { ...event.properties, ...event.context });
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.logger.debug('[analytics] identify', { userId, ...traits });
  }

  reset(): void {
    this.logger.debug('[analytics] reset');
  }
}

class AnalyticsServiceImpl {
  private provider: AnalyticsProvider;
  private logger = LoggerFactory.create();

  constructor(provider?: AnalyticsProvider) {
    this.provider = provider ?? new ConsoleAnalyticsProvider();
  }

  setProvider(provider: AnalyticsProvider): void {
    this.provider = provider;
  }

  track(name: AnalyticsEventName, properties?: Record<string, unknown>, context?: LogContext): void {
    const event: AnalyticsEvent = {
      name,
      properties,
      timestamp: new Date().toISOString(),
      context,
    };
    this.provider.track(event);
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.provider.identify(userId, traits);
  }

  reset(): void {
    this.provider.reset();
  }
}

export const analyticsService = new AnalyticsServiceImpl();
